<template>
  <div class="physical-examination">
    <!-- Header con título y controles -->
    <div class="examination-header">
      <div class="header-title">
        <i class="fas fa-stethoscope"></i>
        <h4>Exploración Física</h4>
      </div>
      <div class="header-controls">
        <button class="unit-toggle-btn" :title="toggleButtonLabel" @click="handleToggleUnits">
          <i class="fas fa-exchange-alt"></i>
          {{ currentUnitLabel }}
        </button>
      </div>
    </div>

    <!-- Formulario Grid -->
    <div class="examination-grid">
      <!-- Signos Vitales -->
      <div class="section-group vitals-section">
        <div class="section-title">
          <div class="title-content">
            <i class="fas fa-heartbeat"></i>
            <span>Signos Vitales</span>
          </div>
        </div>

        <!-- Botones para agregar campos -->
        <div v-if="inactiveVitalSignsFields.length > 0" class="add-fields-section">
          <button
v-for="field in inactiveVitalSignsFields" :key="field.name" class="add-field-btn"
            :title="'Agregar ' + field.label" @click="addVitalSignField(field.name)">
            <i :class="field.icon"></i>
            <span>{{ field.label }}</span>
          </button>
        </div>

        <!-- Campos Activos -->
        <div v-if="activeVitalSignsFields.length > 0" class="vitals-grid">
          <div v-for="field in activeVitalSignsFields" :key="field.name" class="field-group" :class="field.class">
            <div class="field-header">
              <label>
                <i :class="field.icon"></i>
                {{ field.label }}
              </label>
              <button class="close-field-btn" title="Cerrar campo" @click="removeVitalSignField(field.name)">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Presión Arterial (especial) -->
            <div v-if="field.name === 'blood_pressure'" class="bp-inputs">
              <div class="bp-field">
                <span class="bp-chip">SYS</span>
                <input
v-model="formData.systolic" type="number" step="1" min="0" max="300" placeholder="Sistólica"
                  class="form-input bp-input" @input="handleInput('systolic', $event)" />
              </div>
              <span class="separator">/</span>
              <div class="bp-field">
                <span class="bp-chip">DIA</span>
                <input
v-model="formData.diastolic" type="number" step="1" min="0" max="200" placeholder="Diastólica"
                  class="form-input bp-input" @input="handleInput('diastolic', $event)" />
              </div>
              <span class="bp-unit">mmHg</span>
              <div v-if="bloodPressureStatus" :class="['bp-status', bloodPressureStatus.class]">
                <i :class="bloodPressureStatus.icon"></i>
                {{ bloodPressureStatus.text }}
              </div>
            </div>

            <!-- Campos normales -->
            <div v-else class="input-wrapper">
              <input
v-model="formData[field.name]" :type="field.type" :step="field.step" :min="field.min"
                :max="field.max" :placeholder="field.placeholder" class="form-input"
                @input="handleInput(field.name, $event)" />
              <span class="unit-label">{{ field.unit }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Medidas Antropométricas -->
      <div class="section-group anthropometry-section">
        <div class="section-title">
          <div class="title-content">
            <i class="fas fa-ruler-combined"></i>
            <span>Antropometría</span>
          </div>
        </div>

        <!-- Botones para agregar campos -->
        <div v-if="inactiveAnthropometryFields.length > 0" class="add-fields-section">
          <button
v-for="field in inactiveAnthropometryFields" :key="field.name" class="add-field-btn"
            :title="'Agregar ' + field.label" @click="addAnthropometryField(field.name)">
            <i :class="field.icon"></i>
            <span>{{ field.label }}</span>
          </button>
        </div>

        <!-- Campos Activos -->
        <div v-if="activeAnthropometryFields.length > 0" class="anthropometry-grid">
          <div v-for="field in activeAnthropometryFields" :key="field.name" class="field-group" :class="field.class">
            <div class="field-header">
              <label>
                <i :class="field.icon"></i>
                {{ field.label }}
                <span v-if="field.badge" class="unit-badge">{{ field.badge }}</span>
              </label>
              <button
v-if="field.closeable" class="close-field-btn" title="Cerrar campo"
                @click="removeAnthropometryField(field.name)">
                <i class="fas fa-times"></i>
              </button>
            </div>

            <!-- Campo Peso (especial) -->
            <div v-if="field.name === 'weight'">
              <div class="input-wrapper">
                <input
v-model="formData.weight" type="text" :placeholder="weightPlaceholder" class="form-input"
                  @input="handleWeightInput" />
                <span class="unit-label">{{ currentUnitLabel }}</span>
              </div>
              <div v-if="weightInOtherUnit" class="conversion-hint mt-2">
                ≈ {{ weightInOtherUnit }}
              </div>
            </div>

            <!-- Campo IMC (solo lectura) -->
            <div v-else-if="field.name === 'bmi'">
              <div class="input-wrapper imc-display">
                <input :value="bmiValue" type="text" readonly class="form-input" :class="bmiStatusClass" />
                <span class="unit-label">kg/m²</span>
              </div>
              <div v-if="bmiCategory" :class="['bmi-status', bmiCategory.class]">
                <i :class="bmiCategory.icon"></i>
                {{ bmiCategory.text }}
              </div>
            </div>

            <!-- Campo ICC (calculado) -->
            <div v-else-if="field.name === 'waist_hip_ratio'">
              <div class="input-wrapper">
                <input :value="waistHipRatio" type="text" readonly class="form-input" />
              </div>
            </div>

            <!-- Campos normales -->
            <div v-else class="input-wrapper">
              <input
v-model="formData[field.name]" :type="field.type" :step="field.step" :min="field.min"
                :placeholder="field.placeholder" class="form-input" @input="handleInput(field.name, $event)" />
              <span class="unit-label">{{ field.unit }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Autosave Indicator -->
    <div v-if="isSaving" class="autosave-indicator">
      <i class="fas fa-sync fa-spin"></i>
      Guardando...
    </div>
    <div v-else-if="lastSaved" class="autosave-indicator saved mb-4 mt-2">
      <i class="fas fa-check-circle"></i>
      Guardado {{ lastSavedText }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useWeightUnits } from '@/composables/useWeightUnits';
import api from '@/services/api';
import debounce from 'lodash.debounce';

const props = defineProps({
  patientRecord: {
    type: Object,
    required: true
  },
  data: {
    type: Object,
    default: () => ({})
  }
});

// Composable de unidades de peso
const {
  isKg,
  currentUnitLabel,
  toggleButtonLabel,
  weightPlaceholder,
  toggleUnit,
  parseKgWeight,
  formatKgWeight,
  kgToLb,
  lbToKg,
  convertWeight
} = useWeightUnits();

// Estados de expansión/colapso
const isVitalSignsExpanded = ref(false);
const isAnthropometryExpanded = ref(false);

// Estado del formulario
const formData = ref({
  systolic: '',
  diastolic: '',
  heart_rate: '',
  respiratory_rate: '',
  temperature: '',
  oxygen_saturation: '',
  weight: '',
  height: '',
  waist: '',
  hip: ''
});

const isSaving = ref(false);
const lastSaved = ref(null);

// Arrays de campos con estado de visibilidad
const vitalSignsFields = ref([
  {
    name: 'blood_pressure',
    label: 'Presión Arterial',
    icon: 'fas fa-tint',
    class: 'two-columns blood-pressure',
    isActive: false
  },
  {
    name: 'heart_rate',
    label: 'FC',
    icon: 'fas fa-heart',
    placeholder: '60-100',
    unit: 'bpm',
    type: 'number',
    step: '1',
    min: '0',
    isActive: false
  },
  {
    name: 'respiratory_rate',
    label: 'FR',
    icon: 'fas fa-lungs',
    placeholder: '12-20',
    unit: 'rpm',
    type: 'number',
    step: '1',
    min: '0',
    isActive: false
  },
  {
    name: 'temperature',
    label: 'Temperatura',
    icon: 'fas fa-thermometer-half',
    placeholder: '36.5',
    unit: '°C',
    type: 'number',
    step: '0.1',
    min: '0',
    isActive: false
  },
  {
    name: 'oxygen_saturation',
    label: 'SaO₂',
    icon: 'fas fa-lungs',
    placeholder: '95-100',
    unit: '%',
    type: 'number',
    step: '1',
    min: '0',
    max: '100',
    isActive: false
  }
]);

const anthropometryFields = ref([
  {
    name: 'weight',
    label: 'Peso',
    get badge() { return currentUnitLabel.value; },
    icon: 'fas fa-weight',
    type: 'text',
    isActive: false,
    closeable: true
  },
  {
    name: 'height',
    label: 'Talla',
    icon: 'fas fa-ruler-vertical',
    placeholder: 'm.cm (ej: 1.75)',
    unit: 'm',
    type: 'text',
    isActive: false,
    closeable: true
  },
  {
    name: 'bmi',
    label: 'IMC',
    icon: 'fas fa-calculator',
    unit: 'kg/m²',
    type: 'readonly',
    isActive: false,
    closeable: false
  },
  {
    name: 'waist',
    label: 'P. Cintura',
    icon: 'fas fa-circle',
    placeholder: '80.5',
    unit: 'cm',
    type: 'number',
    step: '0.1',
    min: '0',
    isActive: false,
    closeable: true
  },
  {
    name: 'hip',
    label: 'P. Cadera',
    icon: 'fas fa-circle',
    placeholder: '95.5',
    unit: 'cm',
    type: 'number',
    step: '0.1',
    min: '0',
    isActive: false,
    closeable: true
  },
  {
    name: 'waist_hip_ratio',
    label: 'ICC',
    icon: 'fas fa-percentage',
    type: 'readonly',
    isActive: false,
    closeable: false
  }
]);

// Computed para filtrar campos activos e inactivos
const activeVitalSignsFields = computed(() =>
  vitalSignsFields.value.filter(field => field.isActive)
);

const inactiveVitalSignsFields = computed(() =>
  vitalSignsFields.value.filter(field => !field.isActive)
);

const activeAnthropometryFields = computed(() =>
  anthropometryFields.value.filter(field => field.isActive)
);

const inactiveAnthropometryFields = computed(() =>
  anthropometryFields.value.filter(field => !field.isActive)
);

// Funciones para toggle de campos individuales
const toggleVitalSignField = (fieldName, activate) => {
  const field = vitalSignsFields.value.find(f => f.name === fieldName);
  if (field) {
    field.isActive = activate;
  }
};

const toggleAnthropometryField = (fieldName, activate) => {
  const field = anthropometryFields.value.find(f => f.name === fieldName);
  if (field) {
    field.isActive = activate;
  }
};

// Funciones adicionales para consistencia con otros componentes
const addVitalSignField = (fieldName) => {
  toggleVitalSignField(fieldName, true);
};

const removeVitalSignField = (fieldName) => {
  toggleVitalSignField(fieldName, false);
};

const addAnthropometryField = (fieldName) => {
  toggleAnthropometryField(fieldName, true);
};

const removeAnthropometryField = (fieldName) => {
  toggleAnthropometryField(fieldName, false);
};

// Manejo de cambio de unidades
const handleToggleUnits = () => {
  const currentWeight = formData.value.weight;
  if (currentWeight) {
    const fromUnit = isKg.value ? 'kg' : 'lb';
    formData.value.weight = convertWeight(currentWeight, fromUnit);
  }
  toggleUnit();
};

// Validación y formateo de inputs
const handleInput = (field, event) => {
  // Validación básica de números
  const value = event.target.value;
  if (value && isNaN(value)) {
    event.target.value = formData.value[field];
  }
};

const handleWeightInput = (event) => {
  let value = event.target.value;

  if (isKg.value) {
    // Formato kg.g - permitir números y un punto
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
  } else {
    // Formato lb - permitir números y decimales
    value = value.replace(/[^0-9.]/g, '');
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }
    // Limitar a 2 decimales
    if (parts[1] && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }
  }

  formData.value.weight = value;
};

// Cálculo del IMC
const bmiValue = computed(() => {
  const weight = formData.value.weight;
  const height = formData.value.height;

  if (!weight || !height) return '';

  // Convertir peso a kg si está en lb
  let weightKg;
  if (isKg.value) {
    weightKg = parseKgWeight(weight);
  } else {
    weightKg = lbToKg(parseFloat(weight));
  }

  // Parsear altura (formato m.cm)
  const [meters, cm] = String(height).split('.').map(Number);
  const totalHeight = meters + ((cm || 0) / 100);

  if (!weightKg || !totalHeight || totalHeight === 0) return '';

  const bmi = weightKg / (totalHeight * totalHeight);
  return bmi.toFixed(2);
});

// Categoría del IMC
const bmiCategory = computed(() => {
  const bmi = parseFloat(bmiValue.value);
  if (!bmi) return null;

  if (bmi < 18.5) {
    return {
      text: 'Bajo peso',
      class: 'underweight',
      icon: 'fas fa-arrow-down'
    };
  } else if (bmi < 25) {
    return {
      text: 'Peso normal',
      class: 'normal',
      icon: 'fas fa-check'
    };
  } else if (bmi < 30) {
    return {
      text: 'Sobrepeso',
      class: 'overweight',
      icon: 'fas fa-exclamation-triangle'
    };
  } else {
    return {
      text: 'Obesidad',
      class: 'obese',
      icon: 'fas fa-exclamation-circle'
    };
  }
});

const bmiStatusClass = computed(() => {
  return bmiCategory.value ? `bmi-${bmiCategory.value.class}` : '';
});

// Estado de presión arterial
const bloodPressureStatus = computed(() => {
  const sys = parseFloat(formData.value.systolic);
  const dia = parseFloat(formData.value.diastolic);

  if (!sys || !dia) return null;

  if (sys < 120 && dia < 80) {
    return {
      text: 'Normal',
      class: 'bp-normal',
      icon: 'fas fa-check-circle'
    };
  } else if (sys < 130 && dia < 80) {
    return {
      text: 'Elevada',
      class: 'bp-elevated',
      icon: 'fas fa-exclamation-triangle'
    };
  } else if (sys < 140 || dia < 90) {
    return {
      text: 'Hipertensión Etapa 1',
      class: 'bp-stage1',
      icon: 'fas fa-exclamation-circle'
    };
  } else {
    return {
      text: 'Hipertensión Etapa 2',
      class: 'bp-stage2',
      icon: 'fas fa-times-circle'
    };
  }
});

// Conversión de peso en tiempo real
const weightInOtherUnit = computed(() => {
  const weight = formData.value.weight;
  if (!weight) return '';

  if (isKg.value) {
    const totalKg = parseKgWeight(weight);
    const lb = kgToLb(totalKg);
    return `${lb.toFixed(2)} Lb`;
  } else {
    const lb = parseFloat(weight);
    const totalKg = lbToKg(lb);
    return formatKgWeight(totalKg) + ' Kg';
  }
});

// Índice cintura-cadera
const waistHipRatio = computed(() => {
  const waist = parseFloat(formData.value.waist);
  const hip = parseFloat(formData.value.hip);

  if (!waist || !hip || hip === 0) return '';

  return (waist / hip).toFixed(2);
});

// Texto de último guardado
const lastSavedText = computed(() => {
  if (!lastSaved.value) return '';

  const now = new Date();
  const diff = Math.floor((now - lastSaved.value) / 1000); // segundos

  if (diff < 60) return 'hace unos segundos';
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  return `hace ${Math.floor(diff / 3600)} h`;
});

// Guardar datos
const savePhysicalExaminationData = async () => {
  isSaving.value = true;

  const physicalExaminationData = {
    physical_examination: {
      systolic: formData.value.systolic || null,
      diastolic: formData.value.diastolic || null,
      heart_rate: formData.value.heart_rate || null,
      respiratory_rate: formData.value.respiratory_rate || null,
      temperature: formData.value.temperature || null,
      oxygen_saturation: formData.value.oxygen_saturation || null,
      hip: formData.value.hip || null,
      waist: formData.value.waist || null,
      weight: formData.value.weight || null,
      height: formData.value.height || null,
      bmi: bmiValue.value || null,
      weight_unit: isKg.value ? 'kg' : 'lb' // Guardar la unidad usada
    }
  };

  try {
    await api.put(`/pacientes/${props.patientRecord.id}/physical-examination`, physicalExaminationData);
    lastSaved.value = new Date();
  } catch (error) {
    console.error('Error al guardar los datos de exploración física:', error);
  } finally {
    isSaving.value = false;
  }
};

// Debounce del guardado (1 segundo)
const debouncedSave = debounce(savePhysicalExaminationData, 1000);

// Watch para autosave
watch(formData, () => {
  debouncedSave();
}, { deep: true });

// Cargar datos iniciales
watch(() => props.data, (newData) => {
  if (newData && Object.keys(newData).length > 0) {
    formData.value = {
      systolic: String(newData.systolic || ''),
      diastolic: String(newData.diastolic || ''),
      heart_rate: String(newData.heart_rate || ''),
      respiratory_rate: String(newData.respiratory_rate || ''),
      temperature: String(newData.temperature || ''),
      oxygen_saturation: String(newData.oxygen_saturation || ''),
      hip: String(newData.hip || ''),
      waist: String(newData.waist || ''),
      weight: String(newData.weight || ''),
      height: String(newData.height || '')
    };

    // Auto-activar campos de signos vitales que tienen datos
    vitalSignsFields.value.forEach(field => {
      if (field.name === 'blood_pressure') {
        // Activar si cualquiera de los dos tiene valor
        if (formData.value.systolic || formData.value.diastolic) {
          field.isActive = true;
        }
      } else if (formData.value[field.name]) {
        field.isActive = true;
      }
    });

    // Auto-activar campos de antropometría que tienen datos
    anthropometryFields.value.forEach(field => {
      if (field.name === 'bmi') {
        // Activar IMC si hay peso y altura
        if (formData.value.weight && formData.value.height) {
          field.isActive = true;
        }
      } else if (field.name === 'waist_hip_ratio') {
        // Activar ICC si hay cintura y cadera
        if (formData.value.waist && formData.value.hip) {
          field.isActive = true;
        }
      } else if (formData.value[field.name]) {
        field.isActive = true;
      }
    });

    // Auto-expandir secciones si tienen datos
    const hasVitalSignsData = newData.systolic || newData.diastolic || newData.heart_rate ||
      newData.respiratory_rate || newData.temperature || newData.oxygen_saturation;
    if (hasVitalSignsData) isVitalSignsExpanded.value = true;

    const hasAnthropometryData = newData.weight || newData.height || newData.hip || newData.waist;
    if (hasAnthropometryData) isAnthropometryExpanded.value = true;
  }
}, { immediate: true });

</script>
<style scoped>
.physical-examination {
  background: transparent;
  border-radius: 0;
  padding: 0 1.5rem;
}

/* Header */
.examination-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f0f2f5;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.header-title i {
  font-size: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.header-title h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: #344767;
}

.header-controls {
  display: flex;
  gap: 0.75rem;
}

.unit-toggle-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.unit-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.unit-toggle-btn i {
  font-size: 0.875rem;
}

/* Grid Layout */
.examination-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
}

@media (min-width: 900px) {
  .examination-grid {
    grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
    gap: 1.5rem;
  }
}

/* Section Group Styles */
.section-group {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  transition: none;
}

.section-group:hover {
  box-shadow: none;
}

/* Section Title con funcionalidad de colapsar */
.section-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #f0f2f5;
  font-size: 1.125rem;
  font-weight: 600;
  color: #344767;
}

.section-title.collapsible-section-header {
  cursor: pointer;
  padding: 1rem;
  margin: 0;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e9ecef;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.section-title.collapsible-section-header:hover {
  background: linear-gradient(135deg, #667eea10 0%, #764ba210 100%);
  border-color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.section-title .title-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.section-title i {
  font-size: 1.125rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.toggle-section-btn {
  background: transparent;
  border: none;
  color: #6b7280;
  font-size: 1.125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-section-btn:hover {
  color: #667eea;
  transform: scale(1.1);
}

/* Vitals Grid - Diseño responsivo con 2 columnas fijas */
.vitals-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

/* Presión arterial ocupa todo el ancho */
.vitals-grid .full-width {
  grid-column: 1 / -1;
}

/* En pantallas grandes (tablet horizontal+) - mejor distribución */
@media (min-width: 768px) {
  .vitals-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }
}

@media (min-width: 1024px) {
  .vitals-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }
}

/* En pantallas muy grandes */
@media (min-width: 1400px) {
  .vitals-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
}

/* Anthropometry Grid - Diseño responsivo con 2 columnas fijas */
.anthropometry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

/* En pantallas medianas */
@media (min-width: 640px) {
  .anthropometry-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
  }
}

/* En pantallas grandes */
@media (min-width: 1024px) {
  .anthropometry-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1.25rem;
  }
}

/* En pantallas muy grandes */
@media (min-width: 1400px) {
  .vitals-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
  }

  .anthropometry-grid {
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
  }
}

/* En móviles - una sola columna sin scroll */
@media (max-width: 640px) {
  .vitals-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .vitals-grid .full-width {
    grid-column: 1;
  }

  .anthropometry-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

/* Prevenir scroll horizontal en toda la sección */
.physical-examination {
  max-width: 100%;
  overflow-x: hidden;
}

/* Section Groups */
.section-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
  /* Prevenir desbordamiento */
  overflow: hidden;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  margin-bottom: 0.5rem;
}

.section-title i {
  font-size: 1.25rem;
  color: #667eea;
}

.section-title span {
  font-size: 1.125rem;
  font-weight: 700;
  color: #344767;
  letter-spacing: 0.02em;
}

/* Field Groups */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
  background: linear-gradient(150deg, #ffffff 0%, #f6f8ff 100%);
  border: 1px solid #e3e8f4;
  border-radius: 14px;
  padding: 1rem 1rem 1.1rem;
  box-shadow: 0 10px 24px rgba(17, 24, 39, 0.04);
}

.field-group.blood-pressure {
  background: linear-gradient(135deg, #f1f4ff 0%, #ffffff 75%);
  border-color: #d7def8;
}

.field-group label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.field-group label i {
  font-size: 0.875rem;
  color: #667eea;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}

.unit-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
}

/* Input Wrappers */
.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  /* Prevenir desbordamiento */
}

.form-input {
  flex: 1;
  min-width: 0;
  /* Permite que el input se reduzca */
  padding: 0.75rem 1rem;
  padding-right: 3.25rem;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #344767;
  transition: all 0.3s ease;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(17, 24, 39, 0.04);
  box-sizing: border-box;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:read-only {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  cursor: not-allowed;
}

.unit-label {
  position: absolute;
  right: 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #8392ab;
  pointer-events: none;
}

/* Blood Pressure Specific */
.field-group.two-columns {
  grid-column: span 2;
}

.bp-inputs {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  width: 100%;
}

.bp-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.65rem;
  background: #f7f9ff;
  border: 1px solid #dfe5f3;
  border-radius: 12px;
  min-width: 0;
  flex: 1 1 160px;
}

.bp-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #364f8d;
  background: #e8edff;
  border-radius: 8px;
  letter-spacing: 0.01em;
}

.bp-input {
  padding-right: 0.75rem;
  text-align: center;
  border: 1.3px solid #cfd8ec;
  box-shadow: none;
  background: #ffffff;
}

.bp-input:focus {
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.12);
}

.bp-inputs .separator {
  font-size: 1.25rem;
  font-weight: 700;
  color: #6b7280;
  user-select: none;
  padding: 0 0.15rem;
  flex-shrink: 0;
}

.bp-unit {
  font-size: 0.875rem;
  font-weight: 700;
  color: #4f46e5;
  padding: 0.3rem 0.75rem;
  background: #eef2ff;
  border-radius: 10px;
  white-space: nowrap;
  flex-shrink: 0;
  border: 1px solid #dfe4ff;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.bp-inputs .bp-status {
  flex-basis: 100%;
  margin-top: 0.35rem;
}

/* Mejorar legibilidad de labels en campos compactos */
.vitals-grid .field-group label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* En pantallas grandes, restaurar el texto completo */
@media (min-width: 1400px) {
  .vitals-grid .field-group label {
    white-space: normal;
  }
}

.bp-status,
.bmi-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  margin-top: 0.25rem;
}

/* Blood Pressure Status Colors */
.bp-normal {
  background: #d4edda;
  color: #155724;
  border-left: 3px solid #28a745;
}

.bp-elevated {
  background: #fff3cd;
  color: #856404;
  border-left: 3px solid #ffc107;
}

.bp-stage1 {
  background: #ffe5d0;
  color: #cc5500;
  border-left: 3px solid #ff8800;
}

.bp-stage2 {
  background: #f8d7da;
  color: #721c24;
  border-left: 3px solid #dc3545;
}

/* BMI Status Colors */
.bmi-underweight {
  background: #cfe2ff;
  color: #084298;
  border-left: 3px solid #0d6efd;
}

.bmi-normal {
  background: #d4edda;
  color: #155724;
  border-left: 3px solid #28a745;
}

.bmi-overweight {
  background: #fff3cd;
  color: #856404;
  border-left: 3px solid #ffc107;
}

.bmi-obese {
  background: #f8d7da;
  color: #721c24;
  border-left: 3px solid #dc3545;
}

/* IMC Field Styling */
.imc-field .imc-display input {
  font-weight: 700;
  font-size: 1.125rem;
  text-align: center;
}

.bmi-underweight {
  border-color: #0d6efd !important;
  background: #e7f1ff !important;
}

.bmi-normal {
  border-color: #28a745 !important;
  background: #d4edda !important;
}

.bmi-overweight {
  border-color: #ffc107 !important;
  background: #fff8e1 !important;
}

.bmi-obese {
  border-color: #dc3545 !important;
  background: #fce8e8 !important;
}

/* Conversion Hint */
.conversion-hint {
  font-size: 0.8125rem;
  color: #667eea;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  background: linear-gradient(135deg, #f0f3ff 0%, #e8ebff 100%);
  border-radius: 6px;
  border-left: 3px solid #667eea;
}

/* Autosave Indicator */
.autosave-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  margin-top: 1.5rem;
  background: linear-gradient(135deg, #fff3cd 0%, #ffe8a1 100%);
  border-left: 3px solid #ffc107;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #856404;
}

.autosave-indicator.saved {
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
  border-left-color: #28a745;
  color: #155724;
}

.autosave-indicator i {
  font-size: 0.875rem;
}

/* Responsive */
@media (max-width: 768px) {
  .physical-examination {
    padding: 1rem;
  }

  .examination-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .header-title h4 {
    font-size: 1.125rem;
  }

  .unit-toggle-btn {
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
  }

  .examination-grid {
    gap: 1.5rem;
  }

  .section-title {
    padding: 0.625rem 0.875rem;
  }

  .section-title span {
    font-size: 0.9375rem;
  }

  .blood-pressure .bp-inputs {
    gap: 0.35rem;
  }

  .blood-pressure .bp-field {
    flex: 1 1 calc(50% - 0.35rem);
  }
}

/* Estilos para campos individuales agregables/removibles */
.field-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.field-header-compact label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
  margin: 0;
  flex: 1;
}

.close-field-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f8f9fa;
  color: #8392ab;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 1rem;
  flex-shrink: 0;
}

.close-field-btn:hover {
  background: #fee;
  color: #ef4444;
  transform: scale(1.05);
}

.close-field-btn:active {
  transform: scale(0.95);
}

/* Sección de campos inactivos */
.inactive-fields-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px dashed #e9ecef;
}

.inactive-fields-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #8392ab;
  margin-bottom: 1rem;
}

.inactive-fields-title i {
  color: #667eea;
}

.inactive-fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}

.add-field-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: 2px dashed #dee2e6;
  border-radius: 10px;
  background: #f8f9fa;
  color: #8392ab;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.add-field-btn:hover {
  border-color: #667eea;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
  color: #667eea;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
}

.add-field-btn:active {
  transform: translateY(0);
}

.add-field-btn i {
  font-size: 1rem;
}

/* Estilos compactos para gestión de campos */
.add-fields-section {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.add-fields-section .add-field-btn {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #667eea;
  font-size: 0.813rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.add-fields-section .add-field-btn i {
  font-size: 0.75rem;
}

.add-fields-section .add-field-btn:hover {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-color: #667eea;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.field-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px dashed #e1e7f5;
}

@media (max-width: 480px) {
  .physical-examination {
    padding: 0.75rem;
  }

  .form-input {
    padding: 0.625rem 0.875rem;
    font-size: 0.875rem;
  }

  .field-group label {
    font-size: 0.8125rem;
  }
}

/* Number input arrows remove for cleaner look */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 0.5;
}

input[type="number"]:hover::-webkit-inner-spin-button,
input[type="number"]:hover::-webkit-outer-spin-button {
  opacity: 1;
}
</style>
